import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import axios from 'axios';
import { Heart, MapPin, User, Sparkles } from 'lucide-react';
import { Button } from '../components/ui/button';

const fetchPublicProfile = async (slug) => {
  const base = process.env.REACT_APP_BACKEND_URL;
  const res = await axios.get(`${base}/api/public/profile/${slug}`, {
    withCredentials: false,
  });
  return res.data;
};

const PublicProfile = () => {
  const { slug } = useParams();
  const [profile, setProfile] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    document.title = 'Flirty — Member preview';
    (async () => {
      try {
        const p = await fetchPublicProfile(slug);
        setProfile(p);
        document.title = `${p.name || 'Member'} on Flirty`;
      } catch (err) {
        setNotFound(err.response?.status === 404);
      }
    })();
  }, [slug]);

  if (notFound) {
    return (
      <div className="min-h-screen bg-fnl-dark flex items-center justify-center px-4">
        <div className="bg-fnl-purple/80 rounded-2xl p-8 max-w-md text-center" data-testid="public-profile-notfound">
          <User className="w-12 h-12 text-fnl-pink/40 mx-auto mb-3" />
          <h2 className="text-2xl font-bold text-white mb-2">Profile not found</h2>
          <p className="text-purple-300 text-sm mb-4">This member's preview isn't public, or the link is wrong.</p>
          <Link to="/" className="text-fnl-pink hover:underline font-semibold">Go to Flirty home →</Link>
        </div>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="min-h-screen bg-fnl-dark flex items-center justify-center">
        <div className="w-10 h-10 border-4 border-fnl-pink border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-fnl-dark">
      <Helmet prioritizeSeoTags>
        <title>{`${profile.name || 'Member'} on Flirty`}</title>
        <meta
          name="description"
          content={
            (profile.bio || `${profile.name} is on Flirty. Sign up to message ${profile.name?.split(' ')[0] || 'them'} and find local matches.`).slice(0, 160)
          }
        />
        {/* OpenGraph (Facebook, LinkedIn, Discord, WhatsApp, iMessage) */}
        <meta property="og:type" content="profile" />
        <meta property="og:site_name" content="Flirty" />
        <meta
          property="og:title"
          content={`${profile.name}${profile.age ? `, ${profile.age}` : ''}${profile.location ? ` — ${profile.location}` : ''}`}
        />
        <meta
          property="og:description"
          content={
            (profile.bio || `Meet ${profile.name} on Flirty — the place to find local matches.`).slice(0, 200)
          }
        />
        <meta
          property="og:image"
          content={`${process.env.REACT_APP_BACKEND_URL}/api/public/og/image/${slug}`}
        />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="1200" />
        <meta
          property="og:url"
          content={`${process.env.REACT_APP_BACKEND_URL?.replace(/^https?:\/\//, 'https://')}/u/${slug}`}
        />
        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title"
          content={`${profile.name}${profile.age ? `, ${profile.age}` : ''} on Flirty`} />
        <meta name="twitter:description"
          content={(profile.bio || `Meet ${profile.name} on Flirty.`).slice(0, 200)} />
        <meta name="twitter:image"
          content={`${process.env.REACT_APP_BACKEND_URL}/api/public/og/image/${slug}`} />
        {/* Profile-specific OG tags */}
        {profile.name && <meta property="profile:first_name" content={profile.name.split(' ')[0]} />}
        {profile.gender && <meta property="profile:gender" content={profile.gender} />}
      </Helmet>
      {/* Lightweight top bar — no auth state on a public page */}
      <header className="px-5 py-4 border-b border-purple-900/40 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2" data-testid="public-profile-logo">
          <div className="relative">
            <span className="text-white font-bold text-2xl">F</span>
            <Heart className="absolute -top-1 -right-3 w-3.5 h-3.5 fill-fnl-pink text-fnl-pink" />
          </div>
          <span className="text-white font-semibold">Flirty</span>
        </Link>
        <Link to="/">
          <Button variant="ghost" className="text-white hover:text-fnl-pink hover:bg-white/5" data-testid="public-profile-signup-top">
            Sign up free
          </Button>
        </Link>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-10">
        <div className="bg-fnl-purple/60 rounded-3xl border border-purple-900/40 overflow-hidden shadow-2xl shadow-black/40" data-testid="public-profile-card">
          <div className="grid md:grid-cols-2">
            {/* Photo column */}
            <div className="aspect-square md:aspect-auto md:min-h-[420px] bg-fnl-purple-light/40 relative">
              {profile.photo_base64 ? (
                <img src={profile.photo_base64} alt="" className="absolute inset-0 w-full h-full object-cover" />
              ) : (
                <div className="absolute inset-0 flex items-center justify-center">
                  <User className="w-20 h-20 text-fnl-pink/30" />
                </div>
              )}
              <div className="absolute inset-0 bg-gradient-to-t from-fnl-dark/80 via-transparent to-transparent md:hidden" />
              <div className="absolute bottom-3 left-3 md:hidden">
                <h1 className="text-white text-3xl font-extrabold drop-shadow-lg" data-testid="public-profile-name">
                  {profile.name}{profile.age ? `, ${profile.age}` : ''}
                </h1>
              </div>
            </div>

            {/* Info column */}
            <div className="p-6 md:p-8 flex flex-col justify-between gap-6">
              <div>
                <h1 className="hidden md:block text-white text-3xl font-extrabold leading-tight mb-1">
                  {profile.name}{profile.age ? `, ${profile.age}` : ''}
                </h1>
                {profile.location && (
                  <div className="flex items-center gap-1.5 text-purple-300 text-sm mb-4">
                    <MapPin className="w-4 h-4 text-fnl-pink" />
                    <span data-testid="public-profile-location">{profile.location}</span>
                  </div>
                )}
                {profile.bio && (
                  <p className="text-purple-100 text-sm leading-relaxed mb-5 whitespace-pre-wrap" data-testid="public-profile-bio">
                    {profile.bio}
                  </p>
                )}
                {profile.interests?.length > 0 && (
                  <div className="flex flex-wrap gap-1.5 mb-2">
                    {profile.interests.map((i) => (
                      <span key={i} className="px-3 py-1 rounded-full bg-fnl-pink/15 text-fnl-pink text-xs font-semibold border border-fnl-pink/40">
                        {i}
                      </span>
                    ))}
                  </div>
                )}
              </div>

              {/* CTA — the whole reason public profiles exist */}
              <div className="bg-gradient-to-r from-fnl-pink/15 to-purple-500/15 border border-fnl-pink/40 rounded-2xl p-4">
                <div className="flex items-center gap-2 text-fnl-pink text-xs uppercase tracking-wider font-bold mb-2">
                  <Sparkles className="w-3.5 h-3.5" />Want to message {profile.name?.split(' ')[0] || 'them'}?
                </div>
                <p className="text-purple-100 text-sm mb-3">
                  Create a free Flirty account in 30 seconds and start chatting.
                </p>
                <Link to="/">
                  <Button className="w-full h-11 bg-fnl-pink hover:bg-fnl-pink text-white font-bold btn-shadow-pink" data-testid="public-profile-signup-cta">
                    Sign up free
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>

        <p className="text-center text-purple-400 text-xs mt-6">
          This is a public preview. <Link to="/" className="text-fnl-pink hover:underline">Join Flirty</Link> to see full profiles, message members, and find local matches.
        </p>
      </main>
    </div>
  );
};

export default PublicProfile;
